import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { onWastageCreated } from '../events/wastageEvents';
import WastageService from '../services/wastageService';
import { restaurantWastageTotal } from '../utils/restaurantWastageTotal';
import { Bell, Settings, Package, BarChart2, Users, Cpu, QrCode, Trash2, Calendar, Home } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown, faArrowLeft, faBowlFood } from '@fortawesome/free-solid-svg-icons';
import notificationsApi from '../services/notificationsApi';
import { useNotificationCount } from '../services/useNotificationCount';
import { ActionCard, SplitText } from '../styles/standardStyles';
import { useTranslation } from 'react-i18next';
import './Dashboard.css';
import Counter from './Counter';
import ThresholdApprovalPopup from './ThresholdApprovalPopup';
import DailyOperationsCard from './DailyOperationsCard';
import DashboardWastageApprovals from './DashboardWastageApprovals';
import FreeTrialBadge from './FreeTrialBadge';
import DashboardReportsCarousel from './DashboardReportsCarousel';

// --- START: New Modal Component with proper full-width styling ---
const ScanBillModal = ({ onClose, goTo }) => {
  const [isClosing, setIsClosing] = React.useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 200); // Match animation duration
  };

  const modalContent = (
    <div 
      className={`dashboard-scan-modal-overlay ${isClosing ? 'closing' : ''}`}
      onClick={handleClose}
    >
      <div 
        className={`dashboard-scan-modal-box ${isClosing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="dashboard-scan-modal-heading">Scan Bill</h3>

        <button 
          className="dashboard-scan-modal-btn" 
          onClick={() => { handleClose(); setTimeout(() => goTo('stock-in-scan'), 200); }}
        >
          Stock In
        </button>

        <button 
          className="dashboard-scan-modal-btn" 
          onClick={() => { handleClose(); setTimeout(() => goTo('todays-sales-report'), 200); }}
        >
          Total Sales Bill
        </button>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
// --- END: New Modal Component ---

// Simple Date Picker Component for graph carousel
const DatePickerCombined = ({ isOpen, onClose, onWeekSelect, onMonthSelect, defaultPickerMode = 'dates', defaultDateMode = 'single', preselectedRange = null }) => {
  const [pickerMode, setPickerMode] = useState(defaultPickerMode);
  const [dateMode, setDateMode] = useState(defaultDateMode);
    const [viewMonth, setViewMonth] = useState(new Date());
    const [viewYear, setViewYear] = useState(new Date().getFullYear());
    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    const currentToday = new Date();
    const currentYear = currentToday.getFullYear();
    const currentMonth = currentToday.getMonth();
    const isFutureMonthNav = viewMonth.getFullYear() > currentYear || 
        (viewMonth.getFullYear() === currentYear && viewMonth.getMonth() >= currentMonth);

    // Sync focus and selection when picker opens
    useEffect(() => {
        if (isOpen) {
            const now = new Date();
        setPickerMode(defaultPickerMode);
        setDateMode(defaultDateMode);
        // Center calendar on selected start (if provided) else today
        const base = preselectedRange && preselectedRange.start ? new Date(preselectedRange.start) : now;
        setViewMonth(base);
        setViewYear(base.getFullYear());
        // Preselect range when provided
        if (preselectedRange && preselectedRange.start) {
          const s = new Date(preselectedRange.start);
          s.setHours(0,0,0,0);
          setSelectedStartDate(s);
          if (preselectedRange.end) {
            const e = new Date(preselectedRange.end);
            e.setHours(0,0,0,0);
            setSelectedEndDate(e);
          } else {
            setSelectedEndDate(null);
          }
        } else {
          setSelectedStartDate(null);
          setSelectedEndDate(null);
        }
        }
    }, [isOpen, defaultPickerMode, defaultDateMode, preselectedRange]);

    if (!isOpen) return null;

    const formatDate = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const getWeekStart = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = day === 0 ? 6 : day - 1;
        d.setDate(d.getDate() - diff);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const getWeekEnd = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = day === 0 ? 0 : 7 - day;
        d.setDate(d.getDate() + diff);
        d.setHours(23, 59, 59, 999);
        return d;
    };

    const formatWeekDisplay = (week) => {
        const start = new Date(week.start);
        const end = new Date(week.end);
        const monthNamesShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        
        const startMonth = monthNamesShort[start.getMonth()];
        const startDay = start.getDate();
        const endMonth = monthNamesShort[end.getMonth()];
        const endDay = end.getDate();
        const year = end.getFullYear();
        
        if (start.getMonth() === end.getMonth()) {
            return `${startMonth} ${startDay}-${endDay}, ${year}`;
        } else {
            return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
        }
    };

    const navigateMonth = (direction) => {
        const newMonth = new Date(viewMonth);
        newMonth.setMonth(viewMonth.getMonth() + direction);
        setViewMonth(newMonth);
    };

    const navigateYear = (direction) => {
        setViewYear(prevYear => prevYear + direction);
    };

    const getWeeksInMonth = () => {
        const year = viewMonth.getFullYear();
        const month = viewMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        
        const weeks = [];
        let currentWeekStart = getWeekStart(firstDay);
        
        while (currentWeekStart <= lastDay) {
            const weekEnd = getWeekEnd(currentWeekStart);
            const weekStartDate = new Date(currentWeekStart);
            const weekEndDate = new Date(weekEnd);
            
            if (weekEndDate >= firstDay && weekStartDate <= lastDay) {
                weeks.push({
                    start: new Date(weekStartDate),
                    end: new Date(weekEndDate)
                });
            }
            
            currentWeekStart = new Date(currentWeekStart);
            currentWeekStart.setDate(currentWeekStart.getDate() + 7);
        }
        
        return weeks;
    };

    const handleWeekClick = (week) => {
        onWeekSelect({
            start: week.start,
            end: week.end,
            isWeekSelection: true
        });
        onClose();
    };

    const handleMonthClick = (monthIndex) => {
        onMonthSelect({ month: monthIndex, year: viewYear, isMonthSelection: true });
        onClose();
    };

    const handleDateClick = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (date > today) {
            return;
        }
        
        if (dateMode === 'single') {
            const dateStart = new Date(date);
            dateStart.setHours(0, 0, 0, 0);
            const dateEnd = new Date(date);
            dateEnd.setHours(23, 59, 59, 999);
            
            onWeekSelect({
                start: dateStart,
                end: dateEnd,
                isWeekSelection: false,
                isCustomRange: true,
                isSingleDay: true
            });
            onClose();
            return;
        }
        
        if (dateMode === 'range') {
            if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
                setSelectedStartDate(date);
                setSelectedEndDate(null);
            } else if (selectedStartDate && !selectedEndDate) {
                if (date < selectedStartDate) {
                    setSelectedEndDate(selectedStartDate);
                    setSelectedStartDate(date);
                } else {
                    setSelectedEndDate(date);
                }
            }
        }
    };

    const applyDateRange = () => {
        if (selectedStartDate && selectedEndDate) {
            onWeekSelect({
                start: selectedStartDate,
                end: new Date(selectedEndDate.getTime() + 86400000 - 1),
                isWeekSelection: false,
                isCustomRange: true
            });
            setSelectedStartDate(null);
            setSelectedEndDate(null);
            onClose();
        }
    };

    const getDaysInMonth = () => {
        const year = viewMonth.getFullYear();
        const month = viewMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();
        const adjustedStart = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
        
        const days = [];
        
        for (let i = 0; i < adjustedStart; i++) {
            days.push(null);
        }
        
        for (let day = 1; day <= daysInMonth; day++) {
            days.push(new Date(year, month, day));
        }
        
        return days;
    };

    const isDateInRange = (date) => {
        if (!selectedStartDate || !date) return false;
        if (!selectedEndDate) return date.getTime() === selectedStartDate.getTime();
        return date >= selectedStartDate && date <= selectedEndDate;
    };

    const isDateRangeStart = (date) => {
        return selectedStartDate && date && date.getTime() === selectedStartDate.getTime();
    };

    const isDateRangeEnd = (date) => {
        return selectedEndDate && date && date.getTime() === selectedEndDate.getTime();
    };

    const renderWeekGrid = () => {
        const today = new Date();
        const thisWeekStart = getWeekStart(today);
        const weeks = getWeeksInMonth();
      // Normalize preselected week (if provided via props)
      const preSelStart = preselectedRange && preselectedRange.start ? getWeekStart(new Date(preselectedRange.start)) : null;
      const preSelEnd = preselectedRange && preselectedRange.end ? getWeekEnd(new Date(preselectedRange.end)) : null;
        
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '15px', maxHeight: '400px', overflowY: 'auto' }}>
                {weeks.map((week, index) => {
                    const weekStartTime = week.start.getTime();
                    const thisWeekTime = thisWeekStart.getTime();
            const isThisWeek = weekStartTime === thisWeekTime;
            const isSelectedWeek = preSelStart && preSelEnd && pickerMode === 'week' &&
              preSelStart.getTime() === week.start.getTime() && preSelEnd.getTime() === week.end.getTime();
                    const isFutureWeek = week.start > today;
                    const weekLabel = formatWeekDisplay(week);
                    
                    return (
                        <div
                            key={index}
                            onClick={!isFutureWeek ? () => handleWeekClick(week) : undefined}
                            style={{
                                padding: '15px 20px',
                                textAlign: 'center',
                                cursor: isFutureWeek ? 'not-allowed' : 'pointer',
                                borderRadius: '8px',
                  border: (isSelectedWeek || (!preSelStart && isThisWeek)) ? '2px solid #000' : '1px solid #e0e0e0',
                                backgroundColor: isFutureWeek ? '#f5f5f5' : '#fff',
                                color: isFutureWeek ? '#ccc' : '#333',
                  fontWeight: (isSelectedWeek || (!preSelStart && isThisWeek)) ? 'bold' : '500',
                                fontSize: '14px',
                                transition: 'all 0.2s',
                                opacity: isFutureWeek ? 0.5 : 1,
                                pointerEvents: isFutureWeek ? 'none' : 'auto'
                            }}
                        >
                {(isSelectedWeek || (!preSelStart && isThisWeek)) && '✓ '}
                            {weekLabel}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderMonthGrid = () => {
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        const months = [];

        for (let i = 0; i < 12; i++) {
            const isCurrentMonth = (i === currentMonth && viewYear === currentYear);
            const isFutureMonth = (viewYear > currentYear) || (viewYear === currentYear && i > currentMonth);
            
            months.push(
                <div
                    key={i}
                    onClick={() => !isFutureMonth && handleMonthClick(i)}
                    style={{
                        padding: '20px',
                        textAlign: 'center',
                        cursor: isFutureMonth ? 'not-allowed' : 'pointer',
                        borderRadius: '8px',
                        border: isCurrentMonth ? '2px solid #000' : '1px solid #e0e0e0',
                        backgroundColor: isFutureMonth ? '#f5f5f5' : '#fff',
                        color: isFutureMonth ? '#ccc' : '#333',
                        fontWeight: isCurrentMonth ? 'bold' : 'normal',
                        transition: 'all 0.2s',
                        opacity: isFutureMonth ? 0.5 : 1
                    }}
                >
                    {monthNames[i].substring(0, 3)}
                </div>
            );
        }

        return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginTop: '20px' }}>
                {months}
            </div>
        );
    };

    const renderDateCalendar = () => {
        const days = getDaysInMonth();
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        return (
            <div style={{ marginTop: '15px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '15px', padding: '0 5px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px' }}>
                        <button onClick={() => { const newDate = new Date(viewMonth); newDate.setMonth(newDate.getMonth() - 1); setViewMonth(newDate); }} style={{ padding: '6px 12px', fontSize: '16px', fontWeight: 'bold', border: 'none', background: '#f5f5f5', borderRadius: '6px', cursor: 'pointer' }}>‹</button>
                        <span style={{ fontWeight: '600', fontSize: '15px', minWidth: '140px', textAlign: 'center' }}>
                            {monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()}
                        </span>
                        <button 
                            onClick={() => { const newDate = new Date(viewMonth); newDate.setMonth(newDate.getMonth() + 1); setViewMonth(newDate); }} 
                            disabled={isFutureMonthNav}
                            style={{ 
                                padding: '6px 12px', 
                                fontSize: '16px', 
                                fontWeight: 'bold', 
                                border: 'none', 
                                background: '#f5f5f5', 
                                borderRadius: '6px', 
                                cursor: isFutureMonthNav ? 'not-allowed' : 'pointer',
                                color: isFutureMonthNav ? '#ccc' : '#333',
                                opacity: isFutureMonthNav ? 0.5 : 1
                            }}>›</button>
                    </div>
                    
                    <button 
                      onClick={() => { setDateMode(dateMode === 'single' ? 'range' : 'single'); setSelectedStartDate(null); setSelectedEndDate(null); }}
                        style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            fontWeight: '600',
                            border: dateMode === 'range' ? '2px solid #000' : '2px solid #e0e0e0',
                            background: dateMode === 'range' ? '#f5f5f5' : '#fff',
                            color: dateMode === 'range' ? '#000' : '#666',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            width: '100%'
                        }}
                    >
                        {dateMode === 'range' ? 'Range Mode' : 'Single Day'}
                    </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '5px', marginBottom: '10px', padding: '10px 0', borderBottom: '1px solid #e0e0e0' }}>
                    {dayNames.map(day => (
                        <div key={day} style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '12px', color: '#666' }}>
                            {day}
                        </div>
                    ))}
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '5px' }}>
                    {days.map((date, index) => {
                        if (!date) return <div key={`empty-${index}`} />;
                        
                        const isToday = date.getTime() === today.getTime();
                        const highlightToday = dateMode === 'single' && isToday;
                        const isFuture = date > today;
                        const inRange = isDateInRange(date);
                        
                        return (
                            <div
                                key={index}
                                onClick={() => handleDateClick(date)}
                                style={{
                                    padding: '10px',
                                    textAlign: 'center',
                                    cursor: isFuture ? 'not-allowed' : 'pointer',
                                    borderRadius: '4px',
                                    border: inRange ? '2px solid #000' : highlightToday ? '2px solid #000' : 'none',
                                    backgroundColor: isFuture ? '#f9f9f9' : '#fff',
                                    color: isFuture ? '#ccc' : '#333',
                                    fontWeight: (highlightToday || inRange) ? 'bold' : 'normal',
                                    fontSize: '13px',
                                    opacity: isFuture ? 0.4 : 1,
                                    pointerEvents: isFuture ? 'none' : 'auto',
                                    transition: 'all 0.2s'
                                }}
                            >
                                {date.getDate()}
                            </div>
                        );
                    })}
                </div>
                
                {selectedStartDate && selectedEndDate && (
                    <div style={{ marginTop: '15px', textAlign: 'center' }}>
                        <button onClick={applyDateRange} style={{ padding: '10px 30px', backgroundColor: '#4CAF50', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}>
                            Apply Date Range
                        </button>
                    </div>
                )}
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="sales-date-picker-overlay" onClick={onClose}>
            <div className="sales-date-picker-popup" onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: '15px' }}>
                    <button onClick={() => setPickerMode('dates')} style={{ flex: 1, padding: '12px', border: 'none', background: 'transparent', color: pickerMode === 'dates' ? '#000' : '#666', fontWeight: pickerMode === 'dates' ? 'bold' : 'normal', fontSize: '14px', cursor: 'pointer', borderBottom: pickerMode === 'dates' ? '3px solid #000' : 'none', marginBottom: '-2px', transition: 'all 0.2s' }}>
                        Dates
                    </button>
                    <button onClick={() => setPickerMode('week')} style={{ flex: 1, padding: '12px', border: 'none', background: 'transparent', color: pickerMode === 'week' ? '#000' : '#666', fontWeight: pickerMode === 'week' ? 'bold' : 'normal', fontSize: '14px', cursor: 'pointer', borderBottom: pickerMode === 'week' ? '3px solid #000' : 'none', marginBottom: '-2px', transition: 'all 0.2s' }}>
                        Week
                    </button>
                    <button onClick={() => setPickerMode('month')} style={{ flex: 1, padding: '12px', border: 'none', background: 'transparent', color: pickerMode === 'month' ? '#000' : '#666', fontWeight: pickerMode === 'month' ? 'bold' : 'normal', fontSize: '14px', cursor: 'pointer', borderBottom: pickerMode === 'month' ? '3px solid #000' : 'none', marginBottom: '-2px', transition: 'all 0.2s' }}>
                        Month
                    </button>
                </div>

                {pickerMode === 'month' && (
                    <div className="calendar-header">
                        <button onClick={() => navigateYear(-1)} style={{ padding: '8px 12px', fontSize: '18px', fontWeight: 'bold', border: 'none', background: '#f5f5f5', borderRadius: '6px', cursor: 'pointer' }}>‹</button>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{viewYear}</h3>
                        <button 
                            onClick={() => navigateYear(1)} 
                            disabled={viewYear >= currentYear}
                            style={{ 
                                padding: '8px 12px', 
                                fontSize: '18px', 
                                fontWeight: 'bold', 
                                border: 'none', 
                                background: '#f5f5f5', 
                                borderRadius: '6px', 
                                cursor: viewYear >= currentYear ? 'not-allowed' : 'pointer',
                                color: viewYear >= currentYear ? '#ccc' : '#333',
                                opacity: viewYear >= currentYear ? 0.5 : 1
                            }}>›</button>
                    </div>
                )}
                
                {pickerMode === 'week' && (
                    <div className="calendar-header">
                        <button onClick={() => navigateMonth(-1)} style={{ padding: '8px 12px', fontSize: '18px', fontWeight: 'bold', border: 'none', background: '#f5f5f5', borderRadius: '6px', cursor: 'pointer' }}>‹</button>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()}</h3>
                        <button 
                            onClick={() => navigateMonth(1)} 
                            disabled={isFutureMonthNav}
                            style={{ 
                                padding: '8px 12px', 
                                fontSize: '18px', 
                                fontWeight: 'bold', 
                                border: 'none', 
                                background: '#f5f5f5', 
                                borderRadius: '6px', 
                                cursor: isFutureMonthNav ? 'not-allowed' : 'pointer',
                                color: isFutureMonthNav ? '#ccc' : '#333',
                                opacity: isFutureMonthNav ? 0.5 : 1
                            }}>›</button>
                    </div>
                )}

                {pickerMode === 'week' ? renderWeekGrid() : pickerMode === 'month' ? renderMonthGrid() : renderDateCalendar()}
            </div>
        </div>
    );
};

// Chart data will be fetched from backend

// Local ActionCard wrapper to handle navigation
const LocalActionCard = ({ title, icon, screen, onClick, goTo }) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (screen && goTo) {
      goTo(screen);
    }
  };

  return (
    <ActionCard title={title} icon={icon} onClick={handleClick} />
  );
};


// Helper Component for Critical Alerts
const AlertItem = ({ icon, title, description, type, screen, goTo, itemId, itemName, xyzCategory }) => {
  const handleClick = async () => {
    if (!goTo) return;
    const businessId = Number(localStorage.getItem('businessId'));
    if (!businessId) {
      console.error('❌ No businessId found in localStorage');
      return;
    }
    const language = localStorage.getItem('selectedLanguage') || 'en';
    const API_URL = process.env.REACT_APP_API_URL;
    try {
      // Try backend resolver to determine exact destination
      const url = `${API_URL}/minimal-stock/resolve-destination?businessId=${businessId}&name=${encodeURIComponent(itemName || '')}&language=${encodeURIComponent(language)}`;
      const res = await fetch(url);
      if (res.ok) {
        const json = await res.json();
        if (json.success && json.data) {
          const tab = (json.data.tab || '').toString().toUpperCase();
          if (['A','B','C'].includes(tab)) {
            localStorage.setItem('xyz_default_tab', tab);
          }
          if (itemName) localStorage.setItem('xyz_focus_item', itemName);
          const target = json.data.screen || 'xyz';
          return goTo(target);
        }
      }
    } catch (e) {
      // Fallback to local logic below if resolver fails
    }
    // Fallback: use provided category to open XYZ with correct tab
    try {
      const cat = (xyzCategory || '').toString().toUpperCase();
      if (['A','B','C'].includes(cat)) {
        localStorage.setItem('xyz_default_tab', cat);
      }
      if (itemName) localStorage.setItem('xyz_focus_item', itemName);
    } catch {}
    goTo('xyz');
  };

  return (
    <div className={`alert-item alert-${type}`} onClick={handleClick} style={{ cursor: 'pointer' }}>
      <div className="alert-icon">{icon}</div>
      <div className="alert-text">
        <p className="alert-title">{title}</p>
        {
          // If description contains a parenthetical minimum stock note, split it onto its own line
          (() => {
            if (!description) return <p className="alert-description" />;
            const idx = description.indexOf('(');
            if (idx !== -1) {
              const before = description.slice(0, idx).trim();
              const inside = description.slice(idx + 1, description.lastIndexOf(')')).trim();
              if (/minimum/i.test(inside)) {
                const formattedInside = inside.replace(/Minimum Stock should be:?/i, 'Minimum stock:');
                return (
                  <div className="alert-bullets-wrapper">
                    <p className="alert-description alert-bullet">• {before}</p>
                    <p className="alert-minimum alert-bullet">• {formattedInside}</p>
                  </div>
                );
              }
            }
            return <p className="alert-description">{description}</p>;
          })()
        }
      </div>
    </div>
  );
};


// Main Dashboard Component for Mobile View
const DashboardPage = ({ goTo, currentUser }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [stockAlerts, setStockAlerts] = useState({ summary: { total_alerts: 0, critical_items: 0, low_stock_items: 0 }, data: [] });
  const { unreadCount } = useNotificationCount();
  const [loading, setLoading] = useState(true);
  // New: sales/GP state
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [salesToday, setSalesToday] = useState(0);
  const [grossProfitToday, setGrossProfitToday] = useState(0);
  const [cogsToday, setCogsToday] = useState(0);
  const [salesTrendPercent, setSalesTrendPercent] = useState(null);
  // New: chart trend state
  const [trendPeriod, setTrendPeriod] = useState('week'); // 'week' | 'month'
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const [trendData, setTrendData] = useState([]);
  const [loadingTrend, setLoadingTrend] = useState(true);
  // Dynamic wastage
  const [wastageToday, setWastageToday] = useState(null);
  const [wastageWeek, setWastageWeek] = useState(null);
  const [loadingWastage, setLoadingWastage] = useState(false);
  const [wastageError, setWastageError] = useState(null);
  
  // Threshold approval popup state
  const [showApprovalPopup, setShowApprovalPopup] = useState(false);
  const [userRole, setUserRole] = useState(null);

  // Sync businessId and userId from user object to localStorage on mount
  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        
        // Normalize and sync user ID
        const userId = user.id || user.user_id || user.userId;
        if (userId && !localStorage.getItem('userId')) {
          localStorage.setItem('userId', String(userId));
        }
        
        // Normalize and sync business ID
        const userBusinessId = user.businessId || user.business_id || user.businessid;
        const storedBusinessId = localStorage.getItem('businessId');
        
        if (userBusinessId && !storedBusinessId) {
          localStorage.setItem('businessId', String(userBusinessId));
        } else if (userBusinessId && storedBusinessId !== String(userBusinessId)) {
          // Update if businessId has changed (user switched accounts)
          localStorage.setItem('businessId', String(userBusinessId));
        }
      }
    } catch (error) {
      console.error('[Dashboard] ❌ Error syncing user data:', error);
    }
  }, []);

  // Check user role and show approval popup if owner (ONLY if there are pending approvals)
  useEffect(() => {
    
    let timer = null;
    
    const checkRoleAndShowPopup = async () => {
      try {
        const userStr = window.localStorage.getItem('user');
        
        if (!userStr) {
          return;
        }
        
        const user = JSON.parse(userStr);
        
        // Normalize user ID - check multiple possible field names
        const userId = user.id || user.user_id || user.userId;
        const userBusinessId = user.businessId || user.business_id || user.businessid;
        
        
        // Fetch user details including role from backend
        if (userId) {
          const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
          // Remove trailing /api if present to avoid double /api/api
          const baseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
          const response = await fetch(
            `${baseUrl}/api/users/${userId}`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
          
          if (response.ok) {
            const userData = await response.json();
            
            const roleName = userData.data?.role_name?.toLowerCase() || '';
            setUserRole(roleName);
            
            // Check if role contains 'owner' or 'admin' (handles "Owner/Admin", "Manager/Supervisor" etc.)
            if (roleName.includes('owner') || roleName.includes('admin')) {  
              // Get business_id from localStorage with fallbacks
              const storedBusinessId = localStorage.getItem('businessId');
              const effectiveBusinessId = storedBusinessId || userBusinessId;
              
              if (!effectiveBusinessId) {
                console.error('[Dashboard] ❌ No business_id available!');
                return;
              }
              
              // Check if there are any pending approval requests
              const pendingUrl = `${baseUrl}/api/wastage/threshold-approvals/pending?userId=${userId}`;
              
              const pendingResponse = await fetch(pendingUrl, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                  'X-Business-Id': String(effectiveBusinessId)
                }
              });
              
              if (pendingResponse.ok) {
                const pendingResult = await pendingResponse.json();
                const pendingCount = pendingResult.data?.length || 0;
                
                // Only show popup if there are pending approvals
                if (pendingCount > 0) {
                  timer = setTimeout(() => {
                    setShowApprovalPopup(true);
                  }, 2000);
                }
              } else {
                console.error('[Dashboard] Failed to check pending approvals:', pendingResponse.status);
              }
            }
          } else {
            console.error('[Dashboard] Failed to fetch user:', response.status);
          }
        } 
      } catch (e) {
        console.error('[Dashboard] ❌ Error checking user role:', e);
      }
    };
    
    checkRoleAndShowPopup();
    
    // Return cleanup function from useEffect, not from async function
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);



  // Check if user is QR billing user (from backend)
  const [isQRUser, setIsQRUser] = useState(false);

  useEffect(() => {
    const fetchQrBillingStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setIsQRUser(false);
          return;
        }

        const apiUrl = process.env.REACT_APP_API_URL || 'https://be.invexis.co.in/api';
        const response = await fetch(`${apiUrl}/auth/user`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          const qrBilling = data.isQRBilling || data.business?.isQRBilling || false;
          setIsQRUser(!!qrBilling);
        } else {
          setIsQRUser(false);
        }
      } catch (error) {
        console.error('❌ Dashboard: Failed to fetch QR billing status:', error);
        setIsQRUser(false);
      }
    };

    fetchQrBillingStatus();
  }, []);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // Keep legacy period dropdown behavior isolated at Dashboard level
  useEffect(() => {
    if (!showPeriodDropdown) {
      return undefined;
    }

    const handleClickOutside = () => {
      setShowPeriodDropdown(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPeriodDropdown]);

  // Fetch stock alerts from API and check unit mapping
  useEffect(() => {
    const fetchStockAlerts = async () => {
      try {
        setLoading(true);

        // Get businessId from localStorage
        const businessId = Number(localStorage.getItem('businessId'));
    if (!businessId) {
      console.error('❌ No businessId found in localStorage');
      return;
    }

        const API_URL = process.env.REACT_APP_API_URL;
        const response = await fetch(`${API_URL}/minimal-stock/dashboard-alerts/${businessId}`);
        const result = await response.json();

        if (result.success) {
          setStockAlerts(result);
        } else {
          console.error('Failed to fetch stock alerts:', result.error);
        }
      } catch (error) {
        console.error('Error fetching stock alerts:', error);
      } finally {
        setLoading(false);
      }
    };

    const checkUnitMappingOnStartup = async () => {
      try {

        // Get businessId and userId from localStorage
        const businessId = Number(localStorage.getItem('businessId'));
    if (!businessId) {
      console.error('❌ No businessId found in localStorage');
      return;
    }
        const userId = Number(localStorage.getItem('userId')) || 1;

        await notificationsApi.checkUnitMappingSetup(businessId, userId);
      } catch (error) {
        console.error('Error checking unit mapping setup:', error);
      }
    };

    // Run both operations
    fetchStockAlerts();
    checkUnitMappingOnStartup();
  }, []);

  // Fetch wastage records (today & last 7 days) using WastageService
  useEffect(() => {
    const fetchWastage = async () => {
      try {
        setLoadingWastage(true);
        setWastageError(null);

        // Check if user is authenticated
        const token = localStorage.getItem('token');
        const businessId = localStorage.getItem('businessId');

        if (!token || !businessId) {
          console.warn('⚠️ Missing authentication or business ID for wastage data');
          setWastageError('Please log in to view wastage data');
          return;
        }

        const now = new Date();
        const toDate = d => d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
        // Build YYYY-MM-DDTHH:mm:ss for end of day
        const dayEnd = d => {
          const x = new Date(d); x.setHours(23, 59, 59, 999); return x.toISOString();
        };
        const todayStr = toDate(now);
        const weekStart = new Date(); weekStart.setDate(weekStart.getDate() - 6); // last 7 days inclusive
        const weekStartStr = toDate(weekStart);


        // Use WastageService instead of direct fetch for proper business context
        const [todayResult, weekResult] = await Promise.all([
          WastageService.getWastageRecords(todayStr, dayEnd(now), 5000),
          WastageService.getWastageRecords(weekStartStr, dayEnd(now), 5000)
        ]);


        // The restaurant's wastage only: a row with banquet_event_id comes off its
        // banquet's gross profit, never the restaurant's.
        setWastageToday(restaurantWastageTotal(todayResult));
        setWastageWeek(restaurantWastageTotal(weekResult));

      } catch (e) {
        console.error('❌ Wastage fetch error:', e);

        // Handle specific error cases
        if (e.message?.includes('Missing business_id') || e.message?.includes('401') || e.message?.includes('Authentication')) {
          setWastageError('Please log in again');
        } else {
          setWastageToday(0);
          setWastageWeek(0);
          setWastageError(null); // Clear error - this is normal for new accounts
        }
      } finally {
        setLoadingWastage(false);
      }
    };
    fetchWastage(); // initial load
    // subscribe to wastage created events
    const unsubscribe = onWastageCreated(() => {
      fetchWastage();
    });
    return () => { unsubscribe && unsubscribe(); };
  }, []);

  // Fetch yesterday's sales + gross profit (and trend vs the day before)
  useEffect(() => {
    const fmtLocalDate = (d) => d.toLocaleDateString('en-CA'); // YYYY-MM-DD local
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const dateStr = fmtLocalDate(yesterday);
    const API_URL = process.env.REACT_APP_API_URL;
    const base = `${API_URL}/reports`;

    const fetchSummary = async () => {
      try {
        setLoadingSummary(true);

        // Get businessId from localStorage
        const businessId = Number(localStorage.getItem('businessId'));
    if (!businessId) {
      console.error('❌ No businessId found in localStorage');
      return;
    }

        const [sumRes, cmpRes] = await Promise.all([
          fetch(`${base}/summary?date=${encodeURIComponent(dateStr)}&businessId=${businessId}`),
          fetch(`${base}/summary-comparison?date=${encodeURIComponent(dateStr)}&businessId=${businessId}`)
        ]);
        const sumJson = await sumRes.json();
        const cmpJson = await cmpRes.json();
        // sumJson has: total_sales, total_orders, gross_profit, total_cogs, avg_order_value
        const totalSales = Number(sumJson?.total_sales || 0);
        const grossProfit = Number(sumJson?.gross_profit || 0);
        const cogs = Number(sumJson?.total_cogs || 0); // ✅ Use COGS directly from API
        
        setSalesToday(totalSales);
        setGrossProfitToday(grossProfit);
        setCogsToday(cogs);
        // cmpJson.total_sales.percent is string or number
        const pct = Number(cmpJson?.total_sales?.percent ?? NaN);
        setSalesTrendPercent(isNaN(pct) ? null : pct);
      } catch (e) {
        console.error('Error fetching today summary:', e);
        setSalesToday(0);
        setGrossProfitToday(0);
        setCogsToday(0);
        setSalesTrendPercent(0); // Show 0% instead of null for new accounts
      } finally {
        setLoadingSummary(false);
      }
    };

    fetchSummary();
  }, []);

  const formatINR = (n) => {
    try {
      return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(n || 0));
    } catch {
      return String(Math.round(Number(n || 0)));
    }
  };

  // Format stock display with auto unit conversion: |v| < 1000 = g/ml, |v| >= 1000 = kg/L
  // (magnitude-based so negative stock scales the same way as positive)
  const formatStockDisplay = (value, unit) => {
    const v = Number(value || 0);
    const u = (unit || '').toString().trim();
    
    if (!u) return `${v.toFixed(2)}`;
    
    const lc = u.toLowerCase();
    const isKg = lc === 'kg' || lc === 'kilogram' || lc === 'kilograms';
    const isL = lc === 'l' || lc === 'liter' || lc === 'liters' || lc === 'ltr';
    const isG = lc === 'g' || lc === 'gram' || lc === 'grams';
    const isML = lc === 'ml' || lc === 'milliliter' || lc === 'milliliters';
    
    // Helper to format number without unnecessary decimals
    const formatNum = (num) => {
      if (Number.isInteger(num)) return num.toString();
      // Remove trailing zeros: 1.50 → 1.5, 1.00 → 1
      return num.toFixed(2).replace(/\.?0+$/, '');
    };
    
    // Auto-convert kg to g if magnitude in grams would be < 1000
    if (isKg) {
      const grams = v * 1000;
      if (Math.abs(grams) < 1000) {
        return `${formatNum(grams)} g`;
      }
      return `${formatNum(v)} kg`;
    }

    // Auto-convert L to ml if magnitude in ml would be < 1000
    if (isL) {
      const ml = v * 1000;
      if (Math.abs(ml) < 1000) {
        return `${formatNum(ml)} ml`;
      }
      return `${formatNum(v)} L`;
    }

    // For grams: convert to kg if magnitude >= 1000
    if (isG) {
      if (Math.abs(v) >= 1000) {
        const kg = v / 1000;
        return `${formatNum(kg)} kg`;
      }
      return `${formatNum(v)} g`;
    }

    // For ml: convert to L if magnitude >= 1000
    if (isML) {
      if (Math.abs(v) >= 1000) {
        const liters = v / 1000;
        return `${formatNum(liters)} L`;
      }
      return `${formatNum(v)} ml`;
    }
    
    // Default: format number with unit
    return `${formatNum(v)} ${u}`;
  };

  const toWeekdayShort = (label) => {
    try {
      // Ensure consistent parse by appending time; label is YYYY-MM-DD from backend
      const d = new Date(`${label}T00:00:00`);
      if (isNaN(d.getTime())) return label;
      return d.toLocaleDateString('en-US', { weekday: 'short' }); // Mon, Tue, ...
    } catch {
      return label;
    }
  };

  // Normalize labels for trend chart to avoid duplicated "Week Week N" text
  const formatTrendLabel = (period, rawLabel) => {
    const label = String(rawLabel ?? '');
    if (period === 'week') {
      return toWeekdayShort(label);
    }
    if (period === 'month') {
      // If backend already sends "Week 1"/"Week 2", keep as-is (normalize case)
      if (/^\s*week\s*\d+\s*$/i.test(label)) {
        return label.replace(/^\s*week/i, 'Week').trim();
      }
      // If it's just a number like "1" | 1, prefix with Week
      if (/^\s*\d+\s*$/.test(label)) {
        return `Week ${label.trim()}`;
      }
      // Otherwise, return as-is
      return label;
    }
    return label;
  };

  // Normalize trend percent for display and arrow direction
  const normalizedTrend = useMemo(() => {
    if (loadingSummary || salesTrendPercent === null || isNaN(Number(salesTrendPercent))) {
      return { text: '--', dir: 'neutral' };
    }
    const pct = Number(salesTrendPercent);
    // Hide confusing -100% when today's sales are 0; show neutral 0%
    if (salesToday === 0 && pct <= -99.9) {
      return { text: '0%', dir: 'neutral' };
    }
    // Clamp extreme spikes for readability
    const clamped = Math.max(-100, Math.min(999, pct));
    const rounded = Math.round(clamped);
    const dir = rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'neutral';
    return { text: `${rounded}%`, dir };
  }, [loadingSummary, salesTrendPercent, salesToday]);

  // Fetch sales trend for chart
  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_URL;
    const base = `${API_URL}/reports`;
    const fetchTrend = async () => {
      try {
        setLoadingTrend(true);

        // Get businessId from localStorage
        const businessId = Number(localStorage.getItem('businessId'));
    if (!businessId) {
      console.error('❌ No businessId found in localStorage');
      return;
    }

        const res = await fetch(`${base}/trend?period=${encodeURIComponent(trendPeriod)}&businessId=${businessId}`);
        const json = await res.json();
        if (Array.isArray(json)) {
          const mapped = json.map(r => ({
            name: formatTrendLabel(trendPeriod, r.label),
            sales: Number(r.total_sales || 0)
          }));
          setTrendData(mapped);
        } else {
          setTrendData([]);
        }
      } catch (e) {
        console.error('Error fetching sales trend:', e);
        setTrendData([]);
      } finally {
        setLoadingTrend(false);
      }
    };
    fetchTrend();
  }, [trendPeriod]);
  // Get time-based greeting
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    
    if (hour >= 0 && hour < 12) {
      return 'Good Morning';
    } else if (hour >= 12 && hour < 16) {
      return 'Good Afternoon';
    } else {
      return 'Good Evening';
    }
  };

  // Get user's first name or fallback to email or default
  const getUserDisplayName = () => {
    if (!currentUser) return 'User';

    if (currentUser.fullName) {
      // Extract first name from full name
      return currentUser.fullName.split(' ')[0];
    }

    if (currentUser.name) {
      return currentUser.name.split(' ')[0];
    }

    if (currentUser.email) {
      // Extract name from email (before @)
      return currentUser.email.split('@')[0];
    }

    return 'User';
  };

  // Determine user's default dashboard based on email domain
  const getUserDashboardType = () => {
    if (!currentUser || !currentUser.email) return null;
    
    const email = currentUser.email.toLowerCase();
    const domain = email.split('@')[1];
    
    // Check for specific domain patterns
    if (email.includes('@owner.invexis') || email.includes('@gmail.com')) {
      return isQRUser ? 'qr-owner-dashboard' : null;
    } else if (email.includes('@chef.invexis')) {
      return 'chef-dashboard';
    } else if (email.includes('@waiter.invexis')) {
      return 'waiter-dashboard';
    }
    
    return null;
  };

  return (
    <div className="dashboard-container">
      {isModalOpen && <ScanBillModal onClose={closeModal} goTo={goTo} />}

      <header className="dashboard-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <h1 className="header-title">INVEXIS</h1>
          <FreeTrialBadge
            trialExpiryDate={currentUser?.trialExpiryDate}
            subscription={currentUser?.subscription}
            email={currentUser?.email}
            subscriptionSource={currentUser?.subscriptionSource}
          />
          {/* Smart Home Icon - navigates based on user email domain */}
          {(() => {
            const previousDashboard = localStorage.getItem('previousDashboard');
            const dashboardType = getUserDashboardType();

            // Inventory-only users should not see Home icon
            if (!isQRUser && !dashboardType) {
              if (previousDashboard) {
                localStorage.removeItem('previousDashboard');
              }
              return null;
            }

            const canNavigateHome = Boolean(dashboardType || isQRUser || previousDashboard);

            if (!canNavigateHome) return null;

            return (
              <button
                onClick={() => {
                  if (previousDashboard === 'qr-owner-dashboard') {
                    goTo('qr-owner-dashboard');
                  } else if (previousDashboard === 'chef-dashboard') {
                    goTo('chef-dashboard');
                  } else if (previousDashboard === 'waiter-dashboard') {
                    goTo('waiter-dashboard');
                  } else if (dashboardType) {
                    goTo(dashboardType);
                  } else if (isQRUser) {
                    goTo('qr-owner-dashboard');
                  } else {
                    return;
                  }

                  // Clear the previous dashboard after navigation
                  localStorage.removeItem('previousDashboard');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#333',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'color 0.2s',
                  marginLeft: 'auto',
                  marginRight: '8px',
                  paddingRight: '4px'
                }}
                title={
                  previousDashboard === 'qr-owner-dashboard' ? 'Back to Owner Dashboard' :
                  previousDashboard === 'chef-dashboard' ? 'Back to Chef Dashboard' :
                  previousDashboard === 'waiter-dashboard' ? 'Back to Waiter Dashboard' :
                  dashboardType === 'qr-owner-dashboard' ? 'Go to Owner Dashboard' :
                  dashboardType === 'chef-dashboard' ? 'Go to Chef Dashboard' :
                  dashboardType === 'waiter-dashboard' ? 'Go to Waiter Dashboard' :
                  isQRUser ? 'Back to QR Dashboard' : 'Home'
                }
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#000';
                }}
                onMouseLeave={(e) => e.currentTarget.style.color = '#333'}
              >
                <Home size={22} />
              </button>
            );
          })()}
        </div>

        {/* FIX: The Bell and Settings icons are now grouped together here */}
        <div className="header-icons">
          <div className="header-bell-icon" onClick={() => goTo('notifications')}>
            <Bell />
            {unreadCount > 0 && (
              <span className="bell-notification-badge" title={`${unreadCount} unread`}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
          <Settings className="settings-icon" onClick={() => goTo('settings')} />
        </div>
      </header>

      <main className="dashboard-main">
        <div className="welcome-header">
          <SplitText
            text={`${getTimeBasedGreeting()}, ${getUserDisplayName()}!`}
            className="welcome-title"
            style={{ color: 'black' }}
            delay={50}
            duration={1.25}
            ease="power3.out"
            splitType="chars"
            from={{ opacity: 0, y: 40 }}
            to={{ opacity: 1, y: 0 }}
            threshold={0.1}
            rootMargin="-100px"
            textAlign="left"
            tag="h2"
          />
          {/* The Settings icon has been moved from this location */}
        </div>



        <div className="content-grid">
          <div className="grid-row">
            <div className="card sales-card">
              <div className="card-subheader">
                <p className="card-subtitle" style={{ paddingBottom: 0, marginBottom: 0, fontSize: '0.875rem', fontWeight: '500' }}>
                  Yesterday's
                </p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: '0.5rem', fontWeight: '500' }}>
                  <p className="card-subtitle" style={{ paddingBottom: 0, marginBottom: 0, fontSize: '0.875rem' }}>
                    Sales
                  </p>
                  <div className="sales-trend" style={{ position: 'static', transform: 'none', marginTop: 0 }}>
                    {normalizedTrend.dir === 'up' && (
                      <FontAwesomeIcon icon={faArrowUp} className="trend-up-icon" style={{ color: '#22c55e' }} />
                    )}
                    {normalizedTrend.dir === 'down' && (
                      <FontAwesomeIcon icon={faArrowDown} className="trend-down-icon" style={{ color: '#ef4444' }} />
                    )}
                    <span style={{ color: normalizedTrend.dir === 'down' ? '#ef4444' : '#22c55e', fontSize: '1rem' }}>
                      {normalizedTrend.text}
                    </span>
                  </div>
                </div>
              </div>
              <p className="card-main-value">₹{loadingSummary ? '--' : formatINR(salesToday)}</p>
            </div>
            <div className="card">
              <div style={{ marginBottom: '0.5rem' }}>
                <p className="card-subtitle" style={{ paddingBottom: 0, marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>Low Stock</p>
                <p className="card-subtitle" style={{ paddingBottom: 0, marginBottom: 0, fontSize: '0.875rem', fontWeight: '500' }}>Alerts</p>
              </div>
              {loading ? (
                <p className="card-main-value orange-text large-text">{t('common.loading')}</p>
              ) : (
                <p className="card-main-value orange-text large-text">
                  {stockAlerts.summary.total_alerts || 0} {t('dashboard.items')}
                </p>
              )}
            </div>
          </div>

          <DashboardReportsCarousel goTo={goTo} />


          <div className="grid-row">
            <LocalActionCard title={t('dashboard.ingredient_processing')} icon={<FontAwesomeIcon icon={faBowlFood} className="icon-teal" />} screen="itemmap" goTo={goTo} />
            <LocalActionCard title={t('dashboard.wastage_cost')} icon={<Trash2 className="icon-red" />} screen="inventory-stock-out" goTo={goTo} />
          </div>
          <div className="card critical-alerts-card">
            <div className="card-header">
              <h3 className="card-title-strong" style={{ fontSize: '18px' }}>{t('dashboard.critical_alerts')}</h3>
              <span className="link" onClick={() => setShowAllAlerts(!showAllAlerts)}>
                {showAllAlerts ? t('dashboard.show_less') : t('dashboard.view_all')}
              </span>
            </div>
            <div className={`alerts-container ${showAllAlerts ? 'expanded' : ''}`}>
              {loading ? (
                <div className="critical-alerts-loading-state" style={{ gap: '16px', flex: 1, minHeight: '220px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <p style={{
                    color: '#666',
                    fontSize: '14px',
                    textAlign: 'center',
                    margin: 0
                  }}>{t('dashboard.loading_alerts')}</p>
                  <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                </div>
              ) : stockAlerts.data.length === 0 ? (
                <div className="critical-alerts-empty-state">{t('dashboard.no_stock_alerts')}</div>
              ) : (
                <>
                  {showAllAlerts ? (
                    // Show all alerts in scrollable container
                    <div className="alerts-scrollable">
                      {stockAlerts.data.map((alert, index) => (
                        <AlertItem
                          key={alert.item_id}
                          type={alert.urgency_level === 'critical' ? 'critical' : 'warning'}
                          icon={<Package />}
                          title={`${alert.urgency_level === 'critical' ? t('dashboard.critical') : t('dashboard.low')} ${t('dashboard.stock')}: ${alert.name}`}
                          description={`${t('dashboard.current_stock')}: ${formatStockDisplay(alert.current_stock, alert.unit)} (${t('dashboard.reorder_at')}: ${alert.reorder_point ? formatStockDisplay(alert.reorder_point, alert.unit) : 'N/A'})`}
                          screen="inventory"
                          goTo={goTo}
                          itemId={alert.item_id}
                          itemName={alert.name}
                          xyzCategory={alert.abc_category}
                        />
                      ))}
                    </div>
                  ) : (
                    // Show only first 3 alerts
                    <>
                      {stockAlerts.data.slice(0, 3).map((alert, index) => (
                        <AlertItem
                          key={alert.item_id}
                          type={alert.urgency_level === 'critical' ? 'critical' : 'warning'}
                          icon={<Package />}
                          title={`${alert.urgency_level === 'critical' ? t('dashboard.critical') : t('dashboard.low')} ${t('dashboard.stock')}: ${alert.name}`}
                          description={`${t('dashboard.current_stock')}: ${formatStockDisplay(alert.current_stock, alert.unit)} (${t('dashboard.reorder_at')}: ${alert.reorder_point ? formatStockDisplay(alert.reorder_point, alert.unit) : 'N/A'})`}
                          screen="inventory"
                          goTo={goTo}
                          itemId={alert.item_id}
                          itemName={alert.name}
                          xyzCategory={alert.abc_category}
                        />
                      ))}
                      {stockAlerts.data.length > 3 && (
                        <div className="alerts-more-items">
                          {t('dashboard.and')} {stockAlerts.data.length - 3} {t('dashboard.more_items')}...
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Wastage Approvals Panel for Owner */}
          <DashboardWastageApprovals goTo={goTo} />

          {/* Daily Operations Checkpoint Card */}
          <DailyOperationsCard 
            onNavigate={goTo} 
            businessId={currentUser?.businessId || localStorage.getItem('businessId')} 
          />
        </div>
      </main>
      {/** Footer nav is provided globally by App.js (FooterNav). Local nav removed to avoid conflicts. **/}
      
      {/* Threshold Approval Popup for Owner */}
      {(() => {
        const isOwnerOrAdmin = userRole && (userRole.includes('owner') || userRole.includes('admin'));
        return null;
      })()}
      
      {showApprovalPopup && userRole && (userRole.includes('owner') || userRole.includes('admin')) && (
        <ThresholdApprovalPopup 
          onClose={() => {
            setShowApprovalPopup(false);
          }}
        />
      )}
    </div>
  );
};

export default DashboardPage;